import { defineStore } from 'pinia'
import axios from '@axios'
import useJwt from '@/auth/jwt/useJwt'
import { getUserData } from '@/auth/utils'

const pendingStatusRequests = new WeakMap()

const authenticatedAccountId = () => {
  if (!localStorage.getItem(useJwt.jwtConfig.storageTokenKeyName)) return null
  const user = getUserData()
  return user?.id == null ? null : String(user.id)
}

export const useTwoFactorStore = defineStore('TwoFactorStore', {
  state: () => ({
    accountId: null,
    sessionVersion: 0,
    statusVersion: 0,
    statusKnown: false,
    statusLoading: false,
    eligible: false,
    enabled: false,
    promptDismissed: false,
    recoveryCodesRemaining: 0,
    recoverySession: false,
    recoverySessionExpiresAt: 0,
    promptPending: false,
    invitationVisible: false,
    dismissLoading: false,
    dismissError: false,
  }),
  actions: {
    reset() {
      this.sessionVersion += 1
      this.statusVersion += 1
      this.accountId = null
      this.statusKnown = false
      this.statusLoading = false
      this.eligible = false
      this.enabled = false
      this.promptDismissed = false
      this.recoveryCodesRemaining = 0
      this.recoverySession = false
      this.recoverySessionExpiresAt = 0
      this.promptPending = false
      this.invitationVisible = false
      this.dismissLoading = false
      this.dismissError = false
      pendingStatusRequests.delete(this)
    },
    syncAccount() {
      const accountId = authenticatedAccountId()
      if (accountId !== this.accountId) {
        this.reset()
        this.accountId = accountId
      }
      return accountId
    },
    applyStatus(data) {
      if (typeof data?.enabled !== 'boolean' || typeof data?.eligible !== 'boolean') return
      this.statusVersion += 1
      this.statusLoading = false
      pendingStatusRequests.delete(this)
      this.statusKnown = true
      this.enabled = data.enabled
      this.eligible = data.eligible
      this.promptDismissed = data.prompt_dismissed === true
      this.recoveryCodesRemaining = Number.isInteger(data.recovery_codes_remaining)
        ? Math.max(0, data.recovery_codes_remaining) : 0
      this.recoverySession = data.recovery_session === true
      this.recoverySessionExpiresAt = this.recoverySession
        ? Date.now() + Math.min(300, Math.max(0, Number(data.recovery_session_expires_in) || 0)) * 1000 : 0
      if (this.enabled || this.promptDismissed || !this.eligible) this.invitationVisible = false
      if (this.promptPending) {
        this.invitationVisible = this.eligible && !this.enabled && !this.promptDismissed
        this.promptPending = false
      }
    },
    fetchStatus() {
      const accountId = this.syncAccount()
      if (accountId === null) return Promise.resolve(null)
      const pending = pendingStatusRequests.get(this)
      if (pending) return pending
      const sessionVersion = this.sessionVersion
      const statusVersion = this.statusVersion
      this.statusLoading = true
      const isCurrent = () => this.sessionVersion === sessionVersion
        && this.statusVersion === statusVersion
        && this.accountId === accountId && authenticatedAccountId() === accountId
      const request = axios.get('/api/two-factor/status', { timeout: 10000 })
        .then(response => {
          if (!isCurrent()) return null
          if (response.data?.success) {
            this.applyStatus(response.data)
            return response.data
          }
          this.statusKnown = false
          this.invitationVisible = false
          return null
        })
        .catch(() => {
          if (isCurrent()) {
            this.statusKnown = false
            this.invitationVisible = false
          }
          return null
        })
        .finally(() => {
          if (isCurrent()) this.statusLoading = false
          if (pendingStatusRequests.get(this) === request) pendingStatusRequests.delete(this)
        })
      pendingStatusRequests.set(this, request)
      return request
    },
    onAuthenticatedLogin() {
      this.reset()
      if (this.syncAccount() === null) return Promise.resolve(null)
      this.promptPending = true
      return this.fetchStatus()
    },
    closeInvitation() {
      this.invitationVisible = false
      this.promptPending = false
      this.dismissError = false
    },
    clearRecoverySession() {
      this.recoverySession = false
      this.recoverySessionExpiresAt = 0
    },
    async dismissInvitation() {
      if (this.dismissLoading || this.syncAccount() === null) return false
      const sessionVersion = this.sessionVersion
      const accountId = this.accountId
      this.dismissLoading = true
      this.dismissError = false
      try {
        const response = await axios.post('/api/two-factor/dismiss', {}, { timeout: 10000 })
        if (this.sessionVersion !== sessionVersion || this.accountId !== accountId
          || authenticatedAccountId() !== accountId) return false
        if (!response.data?.success) {
          this.dismissError = true
          return false
        }
        this.promptDismissed = true
        this.closeInvitation()
        return true
      } catch (_) {
        if (this.sessionVersion === sessionVersion) this.dismissError = true
        return false
      } finally {
        if (this.sessionVersion === sessionVersion) this.dismissLoading = false
      }
    },
  },
})
